<template>
  <div>
    <base-header type="instore" class="pb-6 pb-8 pt-5 pt-md-8" />
    <div class="container-fluid mt--7">
      <div class="card shadow">
        <div class="card-header bg-transparent">
          <div class="row align-items-center">
            <div class="col-md-5">
              <h3 class="mb-0">Physical Count V2</h3>
            </div>
            <div class="col-md-7 text-right">
              <base-dropdown class="nav-link pr-0 float-right" position="right">
                <div class="media align-items-center" slot="title">
                  <i class="fa fa-ellipsis-v float-right ml-2 btn-sm"></i>
                </div>
                <div class="dropdown-header noti-title">
                  <h6 class="text-overflow m-0">ACTIONS</h6>
                </div>
                <div class="dropdown-item" @click.prevent="openPrintOptions">
                  <i class="fa fa-print"></i>
                  <span>Print</span>
                </div>
                <div v-if="accessRights.includes('G')" class="dropdown-item" @click.prevent="openConfigureDrawer">
                  <i class="ni ni-settings-gear-65"></i>
                  <span>Configure</span>
                </div>
              </base-dropdown>
              <base-button class="float-right ml-1" type="primary" size="sm" @click="optionVisible = true">
                Create Physical Count
              </base-button>
              <base-button class="float-right ml-1" type="default" size="sm" @click="openCountSetDrawer">
                Create Count Set
              </base-button>
            </div>
          </div>
        </div>

        <div class="card-body">
          <base-alert v-if="errorMessage" type="danger" dismissible data-testid="pcv2-error-message">
            {{ errorMessage }}
          </base-alert>
          <base-alert v-if="successMessage" type="success" dismissible>
            {{ successMessage }}
          </base-alert>

          <div class="row align-items-end mb-3">
            <div class="col-md-3">
              <base-input label="Site">
                  <a-select v-model="siteId" showSearch optionFilterProp="children" style="width: 100%" @change="loadDocuments">
                  <a-select-option v-for="site in sites" :key="site.id" :value="site.id">
                    {{ site.name }}
                  </a-select-option>
                </a-select>
              </base-input>
            </div>
            <div class="col-md-3">
              <base-input label="Date Range">
                <a-range-picker v-model="dateRange" style="width: 100%" @change="loadDocuments" />
              </base-input>
            </div>
            <div class="col-md-2">
              <base-input label="Document">
                <a-select v-model="documentKind" style="width: 100%" @change="changeDocumentKind">
                  <a-select-option value="pcount">Pcount Document</a-select-option>
                  <a-select-option value="count_set">Count Set</a-select-option>
                </a-select>
              </base-input>
            </div>
            <div class="col-md-2">
              <base-input label="List">
                <a-select v-model="statusScope" style="width: 100%" @change="loadDocuments">
                  <a-select-option value="all_documents">All</a-select-option>
                  <a-select-option value="unposted_documents">Unposted</a-select-option>
                  <a-select-option value="frozen_documents">Frozen</a-select-option>
                  <a-select-option value="posted_documents">Posted</a-select-option>
                  <a-select-option value="voided_documents">Voided</a-select-option>
                </a-select>
              </base-input>
            </div>
            <div class="col-md-2 text-right">
              <base-button type="secondary" size="sm" @click="loadDocuments">Refresh</base-button>
            </div>
          </div>

          <div class="table-responsive">
            <a-spin :spinning="loading">
              <vuetable
                ref="vuetable"
                v-if="apiURLUpdated"
                :api-url="apiURL"
                :http-options="httpOptions"
                :fields="fields"
                :sort-order="sortOrder"
                :show-sort-icons="true"
                :per-page="20"
                :css="css.table"
                no-data-template="Empty"
                pagination-path=""
                data-path="data"
                @vuetable:load-error="onLoadError"
                @vuetable:pagination-data="onPaginationData"
                @vuetable:loaded="onLoaded"
              >
                <template slot="actions" slot-scope="props">
                  <center>
                    <a-dropdown>
                      <a class="ant-dropdown-link" @click="e => e.preventDefault()">
                        <i class="fa fa-ellipsis-v"></i>
                      </a>
                      <a-menu slot="overlay">
                        <a-menu-item key="open">
                          <a class="btn btn-success btn-sm text-white" @click.prevent="openDocument(props.rowData)">Open</a>
                        </a-menu-item>
                        <a-menu-item key="void" v-if="canVoidDocument(props.rowData)">
                          <a-popconfirm title="You are about to void this record?" @confirm="voidDocument(props.rowData)" okText="Void" cancelText="Cancel">
                            <a data-testid="document-void-action" class="btn btn-danger btn-sm text-white">Void</a>
                          </a-popconfirm>
                        </a-menu-item>
                      </a-menu>
                    </a-dropdown>
                  </center>
                </template>
                <template slot="document_no" slot-scope="props">
                  <a @click.prevent="openDocument(props.rowData)">{{ props.rowData.code_and_document_no || props.rowData.document_no }}</a>
                </template>
                <template slot="created_from" slot-scope="props">
                  <a-tooltip :title="createdFromTooltip(props.rowData)">
                    <span>{{ createdFromLabel(props.rowData) }}</span>
                  </a-tooltip>
                </template>
                <template slot="status" slot-scope="props">
                  {{ statusLabel(props.rowData.status) }}
                </template>
                <template slot="order_status" slot-scope="props">
                  {{ orderStatusLabel(props.rowData.order_status) }}
                </template>
              </vuetable>
            </a-spin>
          </div>
        </div>
        <div class="card-footer d-flex justify-content-end">
          <div class="pagination ui basic segment grid">
            <vuetable-pagination ref="pagination" @vuetable-pagination:change-page="onChangePage" :css="css.pagination" />
          </div>
        </div>
      </div>
    </div>

    <a-drawer title="Physical Count Options" width="520" placement="right" :visible="optionVisible" @close="optionVisible = false">
      <a-list bordered>
        <a-list-item @click="createBlank" data-testid="pcv2-option-blank" class="pcv2-option">Blank Count Sheet</a-list-item>
        <a-list-item @click="openAdvancedFilter" data-testid="pcv2-option-advanced-filter" class="pcv2-option">Auto-Populate via Advance Filter</a-list-item>
        <a-list-item @click="openSelectionDrawer('consolidate')" class="pcv2-option">Consolidate duplicate items into one Physical Count Document</a-list-item>
        <a-list-item @click="openSelectionDrawer('uncounted')" class="pcv2-option">Populate UnCounted Items with Inventory Record</a-list-item>
      </a-list>
    </a-drawer>

    <product-advance-filter
      v-if="advancedFilterVisible"
      :key="advancedFilterKey"
      :parentParams="advancedFilterDisplay"
      @changeParams="applyAdvancedFilter"
      @closeFilter="advancedFilterVisible = false"
    />

    <a-drawer :title="selectionTitle" width="760" placement="right" :visible="selectionVisible" @close="closeSelectionDrawer">
      <div class="row align-items-end mb-3">
        <div class="col-md-8">
          <base-input label="Date Range">
            <a-range-picker v-model="selectionDateRange" style="width: 100%" @change="loadSelectionDocuments" />
          </base-input>
        </div>
        <div class="col-md-4 text-right">
          <base-button type="secondary" size="sm" @click="loadSelectionDocuments">Refresh</base-button>
        </div>
      </div>
      <a-table
        :columns="selectionColumns"
        :dataSource="selectionDocuments"
        :rowKey="record => record.id"
        :loading="selectionLoading"
        :pagination="false"
        :rowSelection="{ selectedRowKeys: selectedDocumentIds, onChange: keys => selectedDocumentIds = keys }"
        size="small"
      />
      <div class="text-right mt-3">
        <base-button type="secondary" size="sm" @click="closeSelectionDrawer">Cancel</base-button>
        <base-button type="primary" size="sm" @click="submitSelection">Ok</base-button>
      </div>
    </a-drawer>

    <a-modal title="Print Physical Count" :visible="printOptionVisible" @cancel="printOptionVisible = false" @ok="openPrintSelectionDrawer">
      <a-radio-group v-model="printOption">
        <a-radio value="counted">Print Counted Items</a-radio>
        <a-radio value="uncounted">Print unCounted Items</a-radio>
      </a-radio-group>
    </a-modal>

    <a-drawer title="" :width="800" placement="right" :visible="configVisible" @close="closeConfigureDrawer">
      <document-configuration
        v-if="configVisible"
        :key="configKey"
        @closeDrawer="closeConfigureDrawer"
        @refreshPage="refreshConfiguration"
      />
    </a-drawer>
  </div>
</template>

<script>
import HTTPRequest from "../../controllers/httpRequest.js";
import secureStorage from "../../controllers/secureStorage";
import ProductAdvanceFilter from "../Products/ProductAdvanceFilter.vue";
import Vuetable from "vuetable-2/src/components/Vuetable";
import VuetablePagination from "vuetable-2/src/components/VuetablePagination";
import DocumentConfiguration from "../Documents/DocumentConfiguration.vue";
import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import { mapState, mapActions } from "vuex";
import { findInvalidNumericFilters } from "../../utils/numericFilterValidation.js";

// Advanced Filter fields whose values are interpolated as raw numbers into the
// backend query. These are validated on the client before any request is sent.
const ADVANCED_FILTER_NUMERIC_KEYS = [
  "retail_price",
  "brand_id",
  "sub_brand_id",
  "product_line_id",
  "product_group_id",
  "product_grade_level",
  "supplier_id",
  "category_id",
  "section_id",
  "division_id",
  "department_id",
];

export default {
  components: { ProductAdvanceFilter, Vuetable, VuetablePagination, DocumentConfiguration },
  data() {
    return {
      loading: false,
      selectionLoading: false,
      optionVisible: false,
      advancedFilterVisible: false,
      selectionVisible: false,
      printOptionVisible: false,
      configVisible: false,
      configKey: 0,
      documents: [],
      selectionDocuments: [],
      selectedDocumentIds: [],
      selectionMode: null,
      printOption: "counted",
      advancedFilterParams: {},
      advancedFilterDisplay: {},
      advancedFilterKey: 0,
      siteId: Number(secureStorage.getItem("defaultSiteId")) || null,
      sites: [],
      dateRange: [],
      selectionDateRange: [],
      documentKind: "pcount",
      statusScope: "all_documents",
      page: 1,
      perPage: 20,
      total: 0,
      errorMessage: null,
      successMessage: null,
      accessRights: [],
      documentTypeDetails: null,
      apiURL: "",
      apiURLUpdated: false,
      fields: [
        "__slot:actions",
        { name: "__slot:document_no", title: "No.", sortField: "document_no" },
        { name: "__slot:created_from", title: "Created From" },
        { name: "document_date", title: "Document Date" },
        { name: "total_quantity", title: "Total Quantity" },
        { name: "__slot:status", title: "Status" },
        { name: "__slot:order_status", title: "Order Status" },
        { name: "created_by", title: "Created By" },
      ],
      sortOrder: [
        {
          field: "id",
          direction: "desc",
        },
      ],
      httpOptions: {
        headers: {
          Authorization: "Bearer " + secureStorage.getItem("accessToken"),
          "Request-Source": "BXI-Web",
          ip: secureStorage.getItem("ipAddress"),
        },
      },
      css: {
        table: {
          tableClass: "table table-sm table-striped table-bordered table-hovered",
          loadingClass: "loading",
        },
        pagination: {
          infoClass: "pull-right",
          wrapperClass: "vuetable-pagination pull-right",
          activeClass: "btn-primary btn-sm",
          disabledClass: "disabled",
          pageClass: "btn btn-border",
          linkClass: "btn btn-border",
          icons: {
            first: "",
            prev: "",
            next: "",
            last: "",
          },
        },
      },
      selectionColumns: [
        { title: "No.", dataIndex: "document_no" },
        { title: "Reference", dataIndex: "reference_1" },
        { title: "Document Date", dataIndex: "document_date" },
        { title: "Total Quantity", dataIndex: "total_quantity" },
        { title: "Status", dataIndex: "status" },
      ],
    };
  },
  computed: {
    ...mapState(["endPoint"]),
    pagination() {
      return { current: this.page, pageSize: this.perPage, total: this.total };
    },
    selectionTitle() {
      if (this.selectionMode === "consolidate") return "Frozen Physical Count Documents";
      if (this.selectionMode === "uncounted") return "Posted Physical Count Documents";
      if (this.selectionMode === "count_set") return "Frozen Physical Count Documents";
      if (this.selectionMode === "print") return "Posted PCount Document";
      return "Posted Physical Count Documents";
    },
  },
  async mounted() {
    await this.loadSites();
    await this.loadDocumentTypeContext();
    this.loadDocuments();
  },
  methods: {
    ...mapActions(["updateCategoriesOptions"]),
    moment,
    // RCG-2 (TC-PCV2-ADF-003/011/013, STA-004/005/006): populate the shared
    // Vuex `categoriesOptions` the Advanced Filter cascader binds to. The hub
    // previously never dispatched this, so the Category dropdown showed
    // "No Data" and blocked every category/status advanced-filter case. Mirrors
    // ProductsV2.vue.
    async loadCategoriesOptions() {
      try {
        const options = await HTTPRequest.getRequest(this.endPoint + "categories/cascader_view", null);
        this.updateCategoriesOptions(Array.isArray(options) ? options : []);
      } catch (e) {
        // Leave the existing options untouched on failure; the drawer still opens.
        console.error("Failed to load category options for Advanced Filter", e);
      }
    },
    async loadSites() {
      const response = await HTTPRequest.getRequest(this.endPoint + "sites/get_valid_sites", null);
      this.sites = Array.isArray(response) ? response : [];
      if (!this.siteId && this.sites.length > 0) this.siteId = this.sites[0].id;
    },
    loadDocuments() {
      this.errorMessage = null;
      this.loading = true;
      this.apiURL = this.physicalCountApiUrl();
      if (!this.apiURLUpdated) {
        this.apiURLUpdated = true;
        return;
      }
      this.$nextTick(() => {
        if (this.$refs.vuetable) this.$refs.vuetable.refresh();
      });
    },
    async changeDocumentKind() {
      await this.loadDocumentTypeContext();
      this.loadDocuments();
    },
    documentKindDetails() {
      if (this.documentKind === "count_set") {
        return {
          transCode: "OTH",
          typeCode: "CNS",
          documentTypeCode: "OTHCNS",
          fallbackName: "Count Set",
          fallbackFriendlyCode: "CNS",
        };
      }

      return {
        transCode: "OTH",
        typeCode: "PCN",
        documentTypeCode: "OTHPCN",
        fallbackName: "Physical Count",
        fallbackFriendlyCode: "PCN",
      };
    },
    async loadDocumentTypeContext() {
      const details = this.documentKindDetails();
      const documentTypes = await HTTPRequest.getRequest(
        `${this.endPoint}document_types?type_code=${details.typeCode}&trans_code=${details.transCode}`,
        null
      );
      const documentType = Array.isArray(documentTypes) && documentTypes.length > 0 ? documentTypes[0] : {};

      this.documentTypeDetails = {
        id: documentType.id,
        name: documentType.name || details.fallbackName,
        code: details.documentTypeCode,
        friendlyCode: documentType.friendly_code || details.fallbackFriendlyCode,
      };

      secureStorage.setItem("documentTypeId", this.documentTypeDetails.id);
      secureStorage.setItem("documentTypeName", this.documentTypeDetails.name);
      secureStorage.setItem("documentTypeCode", this.documentTypeDetails.code);
      secureStorage.setItem("documentTypeFriendlyCode", this.documentTypeDetails.friendlyCode);
      secureStorage.setItem("documentTypeInternalCode", this.documentTypeDetails.code);

      await this.loadAccessRights(this.documentTypeDetails.code);
    },
    async loadAccessRights(documentTypeCode) {
      const systemModules = await HTTPRequest.getRequest(
        `${this.endPoint}system_modules?status=A&code=${documentTypeCode}`,
        null
      );
      const systemModule = Array.isArray(systemModules) && systemModules.length > 0 ? systemModules[0] : null;

      if (!systemModule) {
        this.accessRights = [];
        return;
      }

      const accessRightsRequest = await HTTPRequest.getRequest(
        `${this.endPoint}users/${secureStorage.getItem("userId")}/access_rights?system_module_id=${systemModule.id}`,
        null
      );
      this.accessRights = accessRightsRequest &&
        accessRightsRequest.access_rights &&
        accessRightsRequest.access_rights[systemModule.id]
        ? accessRightsRequest.access_rights[systemModule.id]
        : [];
    },
    async openConfigureDrawer() {
      await this.loadDocumentTypeContext();
      this.configKey++;
      this.configVisible = true;
    },
    closeConfigureDrawer() {
      this.configVisible = false;
    },
    refreshConfiguration() {
      this.closeConfigureDrawer();
      this.successMessage = "Configuration saved.";
      this.loadDocuments();
    },
    physicalCountApiUrl() {
      const params = this.listParams({ page: null, per_page: null });
      const query = Object.keys(params)
        .filter(key => params[key] !== null && params[key] !== undefined && params[key] !== "")
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join("&");
      return `${this.endPoint}physical_count_v2${query ? `?${query}` : ""}`;
    },
    listParams(extra = {}) {
      const params = {
        site_id: this.siteId,
        document_kind: this.documentKind,
        status_scope: this.statusScope,
        page: this.page,
        per_page: this.perPage,
        ...extra,
      };
      if (this.dateRange.length === 2) {
        params.from = moment(this.dateRange[0]).format("YYYY-MM-DD");
        params.to = moment(this.dateRange[1]).format("YYYY-MM-DD");
      }
      return params;
    },
    onLoadError(error) {
      this.loading = false;
      this.errorMessage = error && error.response && error.response.data && error.response.data.error
        ? error.response.data.error
        : "Unable to load Physical Count documents.";
    },
    onLoaded() {
      this.loading = false;
    },
    onPaginationData(paginationData) {
      if (this.$refs.pagination) this.$refs.pagination.setPaginationData(paginationData);
    },
    onChangePage(page) {
      if (this.$refs.vuetable) this.$refs.vuetable.changePage(page);
    },
    selectionParams() {
      const params = { site_id: this.siteId };
      if (this.selectionDateRange.length === 2) {
        params.from = moment(this.selectionDateRange[0]).format("YYYY-MM-DD");
        params.to = moment(this.selectionDateRange[1]).format("YYYY-MM-DD");
      }
      return params;
    },
    handleTableChange(pagination) {
      this.page = pagination.current;
      this.perPage = pagination.pageSize;
      this.loadDocuments();
    },
    async createBlank() {
      this.optionVisible = false;
      this.errorMessage = null;
      // Defer document creation to Save: open a NEW/unsaved PCount form.
      // No document is created and no Document ID is assigned here.
      this.openNewPhysicalCountForm([]);
    },
    async openAdvancedFilter() {
      this.optionVisible = false;
      // RCG-2: ensure the Category cascader has data before the drawer shows.
      await this.loadCategoriesOptions();
      this.advancedFilterVisible = true;
    },
    // Re-open the Advanced Filter drawer, preserving the user's selections
    // (carried back via advancedFilterDisplay). The child drawer hides itself
    // on Apply, so on a validation failure we force a fresh mount here so the
    // user can adjust the filters and apply again.
    reopenAdvancedFilter() {
      this.advancedFilterVisible = false;
      this.$nextTick(() => {
        this.advancedFilterKey += 1;
        this.advancedFilterVisible = true;
      });
    },
    hasSelectedFilters(filters) {
      if (!filters || typeof filters !== "object") return false;
      return Object.keys(filters).some((key) => {
        const value = filters[key];
        if (value === null || value === undefined) return false;
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === "string") return value.trim() !== "";
        if (typeof value === "boolean") return value === true;
        return true;
      });
    },
    async applyAdvancedFilter(params, displayParams) {
      this.errorMessage = null;
      this.advancedFilterParams = params || {};
      this.advancedFilterDisplay = displayParams || {};

      // 1) Require at least one filter before applying. Never run the "no
      //    filters" query that would retrieve ALL items.
      if (!this.hasSelectedFilters(this.advancedFilterParams)) {
        this.errorMessage = "Please select at least one filter before applying.";
        // this.reopenAdvancedFilter();
        this.advancedFilterVisible = false;
        return;
      }

      // 2) Reject invalid numeric values (Infinity, -Infinity, NaN) BEFORE
      //    sending the request. Reusable validation from utilities.js.
      const invalidNumericKeys = findInvalidNumericFilters(this.advancedFilterParams, ADVANCED_FILTER_NUMERIC_KEYS);
      if (invalidNumericKeys.length > 0) {
        this.errorMessage = "Invalid filter value detected. Please review the numeric filters before applying.";
        // this.reopenAdvancedFilter();
        this.advancedFilterVisible = false;
        return;
      }

      this.advancedFilterVisible = false;

      // 3) Preview only: retrieve matching items. This does NOT create a
      //    document and does NOT assign a Document ID.
      const preview = await HTTPRequest.postRequest(this.endPoint + "physical_count_v2/auto_populate", null, {
        site_id: this.siteId,
        filters: this.advancedFilterParams,
        preview: true,
      });

      if (preview && preview.error) {
        this.errorMessage = Array.isArray(preview.error) ? preview.error.join(", ") : preview.error;
        return;
      }

      const rows = (preview && preview.rows) || [];
      const count = preview && preview.count != null ? preview.count : rows.length;

      // 4) Zero results: block immediately. Do not create a document, do not
      //    assign a Document ID, do not open an empty form.
      if (count === 0 || rows.length === 0) {
        this.errorMessage = "No items found that match the selected filter. Please try a different filter selection.";
        return;
      }

      // 5) Confirm the number of items that will be included before populating
      //    the form. Creation is still deferred to Save; OK only populates the
      //    NEW/unsaved PCount form with the matched items.
      this.$confirm({
        title: "Create Physical Count?",
        content: `${count} item(s) will be included in this Physical Count.`,
        okText: "Ok",
        cancelText: "Cancel",
        onOk: () => {
          this.openNewPhysicalCountForm(rows);
        },
      });
    },
    openNewPhysicalCountForm(pcountData) {
      const details = this.documentTypeDetails || {};
      secureStorage.setItem("documentTypeId", details.id);
      secureStorage.setItem("documentTypeName", details.name || "Physical Count");
      secureStorage.setItem("documentTypeCode", details.code || "OTHPCN");
      secureStorage.setItem("documentTypeFriendlyCode", details.friendlyCode || "PCN");
      secureStorage.setItem("documentTypeInternalCode", details.code || "OTHPCN");
      secureStorage.setItem("defaultSiteId", this.siteId);
      this.$router.push({
        name: "document_form",
        params: {
          id: 0,
          document_type_id: details.id,
          document_type_v2: "OTHPCNV2",
          document_typex: details.code || "OTHPCN",
          documentTypeName: details.name || "Physical Count",
          documentTypeCode: details.code || "OTHPCN",
          documentTypeFriendlyCode: details.friendlyCode || "PCN",
          documentSiteId: this.siteId,
          pcount_data: Array.isArray(pcountData) ? pcountData : [],
        },
      });
    },
    openSelectionDrawer(mode) {
      this.printOptionVisible = false;
      this.optionVisible = false;
      this.selectionMode = mode;
      this.selectedDocumentIds = [];
      this.selectionVisible = true;
      this.loadSelectionDocuments();
    },
    openCountSetDrawer() {
      this.openSelectionDrawer("count_set");
    },
    openPrintOptions() {
      this.printOption = "counted";
      this.printOptionVisible = true;
    },
    openPrintSelectionDrawer() {
      this.openSelectionDrawer("print");
    },
    closeSelectionDrawer() {
      this.selectionVisible = false;
      this.selectedDocumentIds = [];
      this.selectionDocuments = [];
    },
    async loadSelectionDocuments() {
      this.selectionLoading = true;
      let endpoint = "posted_documents";
      if (this.selectionMode === "consolidate") endpoint = "frozen_documents";
      if (this.selectionMode === "count_set") endpoint = "frozen_documents";
      const response = await HTTPRequest.getRequest(this.endPoint + `physical_count_v2/${endpoint}`, this.selectionParams());
      this.selectionLoading = false;
      if (response && response.error) {
        this.selectionDocuments = [];
        return;
      }
      this.selectionDocuments = response.data || [];
    },
    async submitSelection() {
      if (this.selectedDocumentIds.length === 0) {
        this.errorMessage = "Please select at least one Physical Count document.";
        return;
      }

      // Print does not create/populate a Physical Count, so it is exempt from
      // the product-count confirmation (like Blank Count Sheet).
      if (this.selectionMode === "print") {
        await this.printSelectedDocuments();
        this.closeSelectionDrawer();
        return;
      }

      const mutation = this.selectionMutationConfig();
      if (!mutation) {
        this.closeSelectionDrawer();
        return;
      }

      this.errorMessage = null;

      // 1) Preview only: ask the backend how many PRODUCTS this action will
      //    create/populate. Reuses the same query the real mutation runs; no
      //    document is created during preview.
      const preview = await HTTPRequest.postRequest(this.endPoint + mutation.url, null, {
        ...mutation.payload,
        preview: true,
      });

      if (preview && preview.error) {
        this.errorMessage = Array.isArray(preview.error) ? preview.error.join(", ") : preview.error;
        return;
      }

      const count = preview && preview.count != null ? preview.count : 0;

      // 2) Zero products: block immediately. Do not create any document.
      if (count === 0) {
        this.$info({
          title: mutation.title,
          content: "No products were found. Nothing will be created.",
        });
        return;
      }

      // 3) Confirm the number of products before running the real mutation.
      this.$confirm({
        title: mutation.title,
        content: `This action will ${mutation.verb} ${count} product${count === 1 ? "" : "s"}. Do you want to continue?`,
        okText: "Continue",
        cancelText: "Cancel",
        onOk: () => {
          this.runSelectionMutation(mutation);
        },
      });
    },
    // Maps the current selection mode to its endpoint, payload, confirmation
    // copy, and success message. Keeps submitSelection free of per-mode
    // branching duplicated between the preview and the real request.
    selectionMutationConfig() {
      if (this.selectionMode === "consolidate") {
        return {
          url: "physical_count_v2/consolidate_duplicates",
          payload: { document_ids: this.selectedDocumentIds },
          title: "Consolidate Duplicate Items",
          verb: "create",
          successMessage: "Consolidated Physical Count created.",
        };
      }
      if (this.selectionMode === "uncounted") {
        return {
          url: "physical_count_v2/populate_uncounted_items",
          payload: { document_ids: this.selectedDocumentIds },
          title: "Populate Uncounted Items",
          verb: "populate",
          successMessage: "Uncounted Physical Count created.",
        };
      }
      if (this.selectionMode === "count_set") {
        return {
          url: "physical_count_v2/count_sets",
          payload: { site_id: this.siteId, document_ids: this.selectedDocumentIds },
          title: "Create Count Set",
          verb: "cover",
          successMessage: "Count Set created.",
        };
      }
      return null;
    },
    async runSelectionMutation(mutation) {
      const response = await HTTPRequest.postRequest(this.endPoint + mutation.url, null, mutation.payload);
      const successMessage =
        this.selectionMode === "uncounted" && !(response && response.id)
          ? "No uncounted inventory items found."
          : mutation.successMessage;
      this.handleMutationResponse(response, successMessage);
      this.closeSelectionDrawer();
    },
    async printSelectedDocuments() {
      const response = await HTTPRequest.postRequest(this.endPoint + "physical_count_v2/print", null, {
        document_ids: this.selectedDocumentIds,
        print_option: this.printOption,
      });
      if (response && response.error) {
        this.errorMessage = response.error;
        return;
      }
      const title = this.printOption === "uncounted" ? "Physical Count - UnCounted Items" : "Physical Count - Counted Items";
      const doc = new jsPDF("l", "pt", "a4");
      doc.text(title, 40, 35);
      doc.autoTable({
        startY: 55,
        head: [["Stock No", "Barcode", "Product", "Counted Qty", "Inventory Qty", "Unit"]],
        body: (response.rows || []).map(row => [
          row.stock_no || "",
          row.barcode || "",
          row.product_name || "",
          row.quantity == null ? "" : row.quantity,
          row.actual_quantity == null ? "" : row.actual_quantity,
          row.unit || "",
        ]),
      });
      doc.save(`${title.replace(/\s+/g, "_")}_${moment().format("YYYYMMDDHHmmss")}.pdf`);
    },
    handleMutationResponse(response, successMessage) {
      if (response && response.error) {
        this.errorMessage = Array.isArray(response.error) ? response.error.join(", ") : response.error;
        this.successMessage = null;
        return;
      }
      this.errorMessage = null;
      this.successMessage = successMessage;
      this.loadDocuments();
      if (response && response.id) this.openDocument(response);
    },
    openDocument(record) {
      const documentType = this.documentTypeRouteDetails(record);
      secureStorage.setItem("documentTypeId", documentType.documentTypeId);
      secureStorage.setItem("documentTypeName", documentType.documentTypeName);
      secureStorage.setItem("documentTypeCode", documentType.documentTypeCode);
      secureStorage.setItem("documentTypeFriendlyCode", documentType.documentTypeFriendlyCode);
      secureStorage.setItem("documentTypeInternalCode", documentType.documentTypeCode);
      this.$router.push({
        name: "document_form",
        params: {
          id: record.id,
          document_type_id: documentType.documentTypeId,
          document_type_v2: documentType.documentTypeCode === "OTHPCN" ? "OTHPCNV2" : "OTHHCNSV2",
          document_typex: documentType.documentTypeCode,
          documentTypeName: documentType.documentTypeName,
          documentTypeCode: documentType.documentTypeCode,
          documentTypeFriendlyCode: documentType.documentTypeFriendlyCode,
          documentSiteId: documentType.documentSiteId,
        },
      });
    },
    documentTypeRouteDetails(record) {
      const rawTypeCode = record.document_type_code || "";
      const transCode = record.document_trans_code || "OTH";
      let documentTypeCode = rawTypeCode.length > 3 && rawTypeCode.substring(0, 3) === transCode
        ? rawTypeCode
        : `${transCode}${rawTypeCode}`;

      if (!rawTypeCode) {
        const name = record.document_type_name || record.document_name || "";
        documentTypeCode = name === "Count Set" ? "OTHCNS" : "OTHPCN";
      }

      const isCountSet = documentTypeCode === "OTHCNS";
      return {
        documentTypeId: record.document_type_id,
        documentTypeName: record.document_type_name || record.document_name || (isCountSet ? "Count Set" : "Physical Count"),
        documentTypeCode,
        documentTypeFriendlyCode: record.document_type_friendly_code || record.friendly_code || (isCountSet ? "CNS" : "PCN"),
        documentSiteId: record.site_id && record.site_id.key ? record.site_id.key : this.siteId,
      };
    },
    canVoidDocument(record) {
      return record.status !== "V" && record.status !== "Voided";
    },
    async voidDocument(record) {
      const response = await HTTPRequest.destroyRequest(this.endPoint + "documents/" + record.id, null, null);
      if (response && response.error) {
        this.errorMessage = Array.isArray(response.error) ? response.error.join(", ") : response.error;
        this.successMessage = null;
        return;
      }
      this.errorMessage = null;
      this.successMessage = "Successfully voided Physical Count #" + (record.document_no || record.id);
      this.loadDocuments();
    },
    statusLabel(status) {
      return { S: "Saved", P: "Posted", V: "Voided", C: "Confirmed", O: "Ongoing" }[status] || status;
    },
    orderStatusLabel(status) {
      return { U: "Unfulfilled", P: "Partial", F: "Frozen/Fulfilled", D: "Deleted", O: "Open", C: "Closed" }[status] || status;
    },
    // Readable label describing how a Physical Count V2 document was created.
    // Prefers the structured source stored in extras.physical_count_source
    // (which retains the full source detail regardless of reference_1's 50-char
    // limit) and falls back to the raw reference_1 for older documents.
    createdFromSource(record) {
      let extras = record && record.extras ? record.extras : null;
      if (typeof extras === "string") {
        try {
          extras = JSON.parse(extras);
        } catch (e) {
          extras = null;
        }
      }
      return extras && extras.physical_count_source ? extras.physical_count_source : null;
    },
    createdFromLabel(record) {
      const source = this.createdFromSource(record);
      if (source && source.type) {
        const labels = {
          auto_populate: "Auto-Populate via Advanced Filter",
          consolidate: "Merged Similar Entries",
          uncounted: "Uncounted Items",
          count_set_duplicates: "Count Set Duplicates",
          count_set_generated: "Generated from Count Set",
        };
        const base = labels[source.type] || "Physical Count";
        const ids = Array.isArray(source.document_ids) ? source.document_ids : [];
        return ids.length > 0 ? `${base} (${ids.length} source doc${ids.length === 1 ? "" : "s"})` : base;
      }
      // Fallback: raw reference_1 (e.g. blank sheet or legacy documents).
      return (record && record.reference_1) || "—";
    },
    createdFromTooltip(record) {
      const source = this.createdFromSource(record);
      if (source && Array.isArray(source.document_ids) && source.document_ids.length > 0) {
        return `Source document IDs: ${source.document_ids.join(", ")}`;
      }
      return (record && record.reference_1) || "";
    },
  },
};
</script>

<style scoped>
.pcv2-option {
  cursor: pointer;
}

.pcv2-option:hover {
  background: #f6f9fc;
}
</style>
